import { pipe } from 'fp-ts/function';
import { isNil } from 'lodash/fp';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import { ActionType, DataTestId, FeatureFlag, SearchType, TextKey } from '@mews-commander/enums';
import {
    BILL_AGGREGATION_TYPE_OPTION_FIELD_NAME,
    BILL_ASSIGNMENT_TARGET_OPTION_FIELD_NAME,
    COMPANY_FIELD_NAME,
    ITEM_ROUTING_OPTION_FIELD_NAME,
    NAME_FIELD_NAME,
    PREPAYMENT_FIELD_NAME,
    RoutingRuleItemRoutingOption,
    SERVICE_FIELD_NAME,
} from '@mews-commander/routing-rules';
import { ApplicationState } from '@mews-commander/types';
import { Card, ModalWrapper, ModalBodyWrapper, SmallContentContainer, FormMetaSection } from '@mews-commander/ui';
import { IconName, Stack, StackItem } from '@optimus-web/core';
import {
    RoutingRuleAssignmentTargetType,
    RoutingRuleBillAggregationType,
    RoutingRulePrepayment,
    useInvalidateRepositoryQueries,
} from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText, useFeatureFlag, useLocalizeText, withFeatureFlagService } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { Option } from '@mews-commander/legacy-data-structures';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    commanderForm,
    InputField,
    presence,
    RadioGroupField,
    SelectField,
    validation,
} from '@mews-commander/legacy-form';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getRoutingRule } from '@mews-commander/legacy-entities/src/ducks/routingRules';
import { getEnterpriseServiceOptions } from '@mews-commander/legacy-entities/src/ducks/services';
import { getEnterpriseDetailUrl } from '@mews-commander/legacy-entities/src/selectors/enterprises';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import MultiTypeSearchFormField from '../../../components/form/MultiTypeSearchFormField';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { resetSearch } from '../../search/actions';
import { getSelectedSearchResult } from '../../search/selectors';
import { deleteRoutingRuleHeadingAction } from '../actions';
import { getRoutingRuleIndexUrl } from '../selectors';
import ServiceItemRoutingSection from './ServiceItemRoutingSection';

const nameProps = {
    label: <LocalizedText textKey={TextKey.Name} />,
    name: NAME_FIELD_NAME,
    required: true,
};

const companyProps = {
    label: <LocalizedText textKey={TextKey.RoutingForCompanyOrTravelAgencyContractLabel} />,
    name: COMPANY_FIELD_NAME,
    searchTypes: [SearchType.TravelAgency, SearchType.Company],
    required: true,
    hint: <LocalizedText textKey={TextKey.RoutingRuleCompanyHint} />,
    helper: <LocalizedText textKey={TextKey.RoutingForCompanyOrTravelAgencyContractHelperText} />,
};

const itemRoutingOptionProps = {
    label: <LocalizedText textKey={TextKey.WhatToRoute} />,
    name: ITEM_ROUTING_OPTION_FIELD_NAME,
    required: true,
    options: [
        Option({
            value: RoutingRuleItemRoutingOption.AllItems,
            label: <LocalizedText textKey={TextKey.AllStayItems} />,
        }),
        Option({
            value: RoutingRuleItemRoutingOption.OnlySelected,
            label: <LocalizedText textKey={TextKey.OnlySelectedStayItems} />,
        }),
    ],
};

const companyRoutingOptionProps = {
    label: <LocalizedText textKey={TextKey.HowToRoute} />,
    name: BILL_ASSIGNMENT_TARGET_OPTION_FIELD_NAME,
    required: true,
    options: [
        Option({
            value: RoutingRuleAssignmentTargetType.CompanyAsDetails,
            label: <LocalizedText textKey={TextKey.HowToRouteAssociatedWithCompany} />,
        }),
        Option({
            value: RoutingRuleAssignmentTargetType.CompanyAsOwner,
            label: <LocalizedText textKey={TextKey.HowToRouteOwnedByCompany} />,
        }),
    ],
};

const prepaymentProps = {
    label: <LocalizedText textKey={TextKey.ApplyRuleTo} />,
    name: PREPAYMENT_FIELD_NAME,
    required: true,
    options: [
        Option({
            value: RoutingRulePrepayment.All,
            label: <LocalizedText textKey={TextKey.RoutingRulePrepaymentAllLabel} />,
        }),
        Option({
            value: RoutingRulePrepayment.Prepaid,
            label: <LocalizedText textKey={TextKey.RoutingRulePrepaymentPrepaidLabel} />,
        }),
    ],
};

const getConstraints = () => ({
    [SERVICE_FIELD_NAME]: { presence },
    [COMPANY_FIELD_NAME]: { presence },
    [ITEM_ROUTING_OPTION_FIELD_NAME]: {
        presence,
    },
    [PREPAYMENT_FIELD_NAME]: { presence },
    [BILL_ASSIGNMENT_TARGET_OPTION_FIELD_NAME]: { presence },
    [NAME_FIELD_NAME]: { presence },
});

const getSearchScope = (form: $TSFixMe) => `${form}-owner-search`;

const FormContent = ({ enterpriseId, initialValues, form, routingRuleMeta }: $TSFixMe) => {
    const { company } = initialValues;
    const formSelector = formValueSelector(form);
    const serviceId = useCommanderSelector((state) => formSelector(state, SERVICE_FIELD_NAME));
    const serviceOptions = useCommanderSelector((state) => getEnterpriseServiceOptions(state, { enterpriseId }));
    const isNewRoutingRulesOnAddEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesOnAddEnabled);

    return (
        <Card>
            <Stack vertical spacing="300">
                {isNewRoutingRulesOnAddEnabled ? <InputField {...nameProps} /> : null}
                <SelectField
                    name={SERVICE_FIELD_NAME}
                    label={<LocalizedText textKey={TextKey.SelectService} />}
                    options={serviceOptions}
                    required
                    data-test-id={DataTestId.ServiceSelector}
                    size="medium"
                    helper={<LocalizedText textKey={TextKey.SelectServiceRoutingRulesHelperText} />}
                />
                <MultiTypeSearchFormField
                    {...companyProps}
                    enterpriseId={enterpriseId}
                    scope={getSearchScope(form)}
                    form={form}
                    size="medium"
                    preselectedResult={company}
                    optionalSearchParams={{ serviceIds: [serviceId] }}
                />

                <StackItem>
                    <RadioGroupField {...itemRoutingOptionProps} disabled={!serviceId} />
                    <ServiceItemRoutingSection form={form} />
                </StackItem>
                <RadioGroupField {...companyRoutingOptionProps} />

                <RadioGroupField {...prepaymentProps} />
                {isNil(routingRuleMeta) ? null : <FormMetaSection meta={routingRuleMeta} />}
            </Stack>
        </Card>
    );
};

export const RoutingRuleForm = pipe(
    FormContent,
    commanderForm({
        validate: validation(getConstraints),
        submitButtonProps: { variant: 'primary' },
        onChange: (
            { [SERVICE_FIELD_NAME]: serviceId, [BILL_ASSIGNMENT_TARGET_OPTION_FIELD_NAME]: targetOption },
            dispatch: $TSFixMe,
            { form }: $TSFixMe,
            { [SERVICE_FIELD_NAME]: prevServiceId }
        ) => {
            if (serviceId && prevServiceId && serviceId !== prevServiceId) {
                dispatch(change(form, COMPANY_FIELD_NAME, null));
                dispatch(resetSearch(getSearchScope(form)));
            }
            if (targetOption === RoutingRuleAssignmentTargetType.CompanyAsDetails) {
                dispatch(
                    change(
                        form,
                        BILL_AGGREGATION_TYPE_OPTION_FIELD_NAME,
                        RoutingRuleBillAggregationType.OnePerReservation
                    )
                );
            }
        },
    }),
    withFeatureFlagService
);

const RoutingRuleFormWrapper = ({ children, enterpriseId, routingRuleId, form }: $TSFixMe) => {
    const localizeText = useLocalizeText();
    const routingRuleLocalized = localizeText({ textKey: TextKey.RoutingRule });
    const [entityTitle, changeEntityTitle] = useState(routingRuleLocalized);
    const dispatch = useDispatch();
    const resolveRoute = useResolveRoute();
    const isNewRoutingRulesOnAddEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesOnAddEnabled);
    const invalidateQuery = useInvalidateRepositoryQueries();

    const enterpriseBreadcrumbName = useSelector((state: ApplicationState) =>
        getEnterpriseName(state, { enterpriseId })
    );
    const enterpriseBreadcrumbUrl = useSelector((state) =>
        getEnterpriseDetailUrl(state, { enterpriseId, resolveRoute })
    );

    const routingRulesIndexBreadcrumbUrl = useSelector((state) =>
        getRoutingRuleIndexUrl(state, { enterpriseId, resolveRoute })
    );

    const searchResult = useSelector((state) => {
        if (routingRuleId && form) {
            return getSelectedSearchResult(state, { scope: getSearchScope(form) });
        }
        return null;
    });

    const routingRule = useSelector((state: ApplicationState) => {
        if (routingRuleId) {
            return getRoutingRule(state, { id: routingRuleId, enterpriseId });
        }
        return {};
    });

    useEffect(() => {
        // @ts-expect-error $TSFixMeError: Object is of type 'unknown'.
        if (searchResult && (searchResult as $TSFixMe).id === routingRule.companyId) {
            changeEntityTitle(
                `${(searchResult as $TSFixMe).title}${UnicodeEntity.NoBreakSpace}${routingRuleLocalized}`
            );
        }
    }, [searchResult, routingRule, routingRuleLocalized]);

    const breadcrumbs: Breadcrumbs = [
        {
            text: enterpriseBreadcrumbName,
            url: enterpriseBreadcrumbUrl,
        },
        {
            text: (
                <LocalizedText
                    textKey={
                        isNewRoutingRulesOnAddEnabled ? TextKey.BillingAutomationPlural : TextKey.RoutingRulePlural
                    }
                />
            ),
            url: routingRulesIndexBreadcrumbUrl,
        },
        {
            text: (
                <LocalizedText
                    textKey={isNewRoutingRulesOnAddEnabled ? TextKey.RoutingRuleLegacyBreadcrumb : TextKey.RoutingRule}
                />
            ),
            active: true,
        },
    ];

    const actions: HeadingAction[] = routingRuleId
        ? [
              {
                  actionId: 'deleteRule',
                  onConfirmation: () =>
                      dispatch(
                          deleteRoutingRuleHeadingAction({ routingRuleId, enterpriseId, entityTitle, invalidateQuery })
                      ),
                  type: ActionType.ConfirmationDialog,
                  buttonText: <LocalizedText textKey={TextKey.Delete} />,
                  text: <LocalizedText textKey={TextKey.DeleteHint} />,
                  title: <LocalizedText textKey={TextKey.DeleteConfirmation} substitutes={{ entity: entityTitle }} />,
                  icon: IconName.Delete,
                  isDanger: true,
              },
          ]
        : [];

    return (
        <ModalWrapper>
            <ModalHeader
                title={<LocalizedText textKey={TextKey.RoutingRule} />}
                breadcrumbs={breadcrumbs}
                actions={actions}
            />
            <ModalBodyWrapper padded>
                <SmallContentContainer condensed>{children}</SmallContentContainer>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};

export default RoutingRuleFormWrapper;
